import type { PlanColumn, PlanDefinition, PlanDraft } from "@weights/plan-reading";
import type { DefinitionReading } from "./reading.js";
import type { Block } from "./clipboard.js";
import { cellOf, newColumnID, sameChips } from "./draft.js";
import { displayLabel, labelKey } from "./lib.js";
import { type Change, change, isNoop } from "./patch.js";
import { type Rect, ROWS, cellsOf, rectSize } from "./selection.js";

export interface CellUpdate {
  r: number;
  c: number;
  chips: string[];
}

export function setCells(draft: PlanDraft, updates: CellUpdate[]): Change[] {
  const out: Change[] = [];
  for (const u of updates) {
    const column = draft.columns[u.c];
    if (!column || u.r < 0 || u.r >= ROWS) continue;
    if (sameChips(cellOf(draft, u.r, u.c), u.chips)) continue;
    out.push(change(draft, ["days", u.r, "cells", column.id], u.chips.length ? u.chips : undefined));
  }
  return out;
}

export function fillRect(draft: PlanDraft, rect: Rect, chips: string[]): Change[] {
  return setCells(
    draft,
    cellsOf(rect).map((p) => ({ ...p, chips })),
  );
}

export function clearRect(draft: PlanDraft, rect: Rect): Change[] {
  return fillRect(draft, rect, []);
}

export function copyBlock(draft: PlanDraft, rect: Rect): Block {
  const block: Block = [];
  for (let r = rect.top; r <= rect.bottom; r++) {
    const row: string[][] = [];
    for (let c = rect.left; c <= rect.right; c++) row.push([...cellOf(draft, r, c)]);
    block.push(row);
  }
  return block;
}

/** Cmd+D: the range's top row down through it; a one-row range copies the row above. */
export function fillDown(draft: PlanDraft, rect: Rect): { changes: Change[]; rect: Rect } {
  const source = rect.top === rect.bottom ? rect.top - 1 : rect.top;
  const target = rect.top === rect.bottom ? rect : { ...rect, top: rect.top + 1 };
  if (source < 0) return { changes: [], rect };
  const updates: CellUpdate[] = [];
  for (let r = target.top; r <= target.bottom; r++)
    for (let c = rect.left; c <= rect.right; c++) updates.push({ r, c, chips: [...cellOf(draft, source, c)] });
  return { changes: setCells(draft, updates), rect: { ...rect, top: Math.min(source, rect.top) } };
}

/** Cmd+R: the range's left column across it; a one-column range copies the column to its left. */
export function fillRight(draft: PlanDraft, rect: Rect): { changes: Change[]; rect: Rect } {
  const source = rect.left === rect.right ? rect.left - 1 : rect.left;
  const target = rect.left === rect.right ? rect : { ...rect, left: rect.left + 1 };
  if (source < 0) return { changes: [], rect };
  const updates: CellUpdate[] = [];
  for (let r = rect.top; r <= rect.bottom; r++)
    for (let c = target.left; c <= target.right; c++) updates.push({ r, c, chips: [...cellOf(draft, r, source)] });
  return { changes: setCells(draft, updates), rect: { ...rect, left: Math.min(source, rect.left) } };
}

/** The fill handle: the source range repeated over the larger target, no series inferred. */
export function fillTiled(draft: PlanDraft, source: Rect, target: Rect): Change[] {
  const block = copyBlock(draft, source);
  const { rows, cols } = rectSize(source);
  const updates: CellUpdate[] = [];
  for (let r = target.top; r <= target.bottom; r++)
    for (let c = target.left; c <= target.right; c++) {
      const br = (((r - source.top) % rows) + rows) % rows;
      const bc = (((c - source.left) % cols) + cols) % cols;
      updates.push({ r, c, chips: [...(block[br]?.[bc] ?? [])] });
    }
  return setCells(draft, updates);
}

export interface PasteResult {
  changes: Change[];
  rect: Rect;
  rows: number;
  cols: number;
  clippedRows: number;
  clippedCols: number;
}

/**
 * One cell onto a range fills it; a block whose size divides the range tiles it; otherwise the
 * block spills down and right from the range's corner, clipped to the grid.
 */
export function pasteBlock(draft: PlanDraft, rect: Rect, block: Block): PasteResult {
  const rows = block.length;
  const cols = Math.max(0, ...block.map((r) => r.length));
  const columns = draft.columns.length;
  const empty = { changes: [], rect, rows, cols, clippedRows: 0, clippedCols: 0 };
  if (rows === 0 || cols === 0 || columns === 0) return empty;
  const size = rectSize(rect);
  if (rows === 1 && cols === 1) return { ...empty, changes: fillRect(draft, rect, block[0]![0] ?? []) };
  if (size.rows % rows === 0 && size.cols % cols === 0 && (size.rows > 1 || size.cols > 1)) {
    const updates: CellUpdate[] = cellsOf(rect).map((p) => ({ ...p, chips: [...(block[(p.r - rect.top) % rows]?.[(p.c - rect.left) % cols] ?? [])] }));
    return { ...empty, changes: setCells(draft, updates) };
  }
  const bottom = Math.min(ROWS - 1, rect.top + rows - 1);
  const right = Math.min(columns - 1, rect.left + cols - 1);
  const updates: CellUpdate[] = [];
  for (let r = rect.top; r <= bottom; r++)
    for (let c = rect.left; c <= right; c++) updates.push({ r, c, chips: [...(block[r - rect.top]?.[c - rect.left] ?? [])] });
  return {
    changes: setCells(draft, updates),
    rect: { top: rect.top, left: rect.left, bottom, right },
    rows,
    cols,
    clippedRows: rect.top + rows - 1 - bottom,
    clippedCols: rect.left + cols - 1 - right,
  };
}

// MARK: Columns

export function addColumn(draft: PlanDraft, name: string, index = draft.columns.length): { changes: Change[]; id: string } {
  const id = newColumnID(draft.columns.map((c) => c.id));
  const columns = [...draft.columns];
  columns.splice(index, 0, { id, name });
  return { changes: [change(draft, ["columns"], columns)], id };
}

export function renameColumn(draft: PlanDraft, c: number, name: string): Change[] {
  const column = draft.columns[c];
  if (!column || column.name === name) return [];
  return [change(draft, ["columns", c, "name"], name)];
}

export function duplicateColumn(draft: PlanDraft, c: number, name: string): { changes: Change[]; id: string } {
  const column = draft.columns[c];
  if (!column) return { changes: [], id: "" };
  const { changes, id } = addColumn(draft, name, c + 1);
  if (column.target) {
    const columns = (changes[0]!.after as PlanColumn[]).map((x) => (x.id === id ? { ...x, target: structuredClone(column.target) } : x));
    changes[0] = change(draft, ["columns"], columns);
  }
  for (let r = 0; r < draft.days.length; r++) {
    const chips = draft.days[r]!.cells[column.id];
    if (chips?.length) changes.push(change(draft, ["days", r, "cells", id], chips));
  }
  return { changes, id };
}

export function moveColumn(draft: PlanDraft, c: number, by: number): Change[] {
  const to = c + by;
  if (!draft.columns[c] || to < 0 || to >= draft.columns.length) return [];
  const columns = [...draft.columns];
  const [moved] = columns.splice(c, 1);
  columns.splice(to, 0, moved!);
  return [change(draft, ["columns"], columns)];
}

export function deleteColumn(draft: PlanDraft, c: number): Change[] {
  const column = draft.columns[c];
  if (!column) return [];
  const out: Change[] = [];
  draft.days.forEach((day, r) => {
    if (day.cells[column.id] !== undefined) out.push(change(draft, ["days", r, "cells", column.id], undefined));
  });
  out.push(change(draft, ["columns"], draft.columns.filter((_, i) => i !== c)));
  return out;
}

// MARK: Days, title

export function setLocation(draft: PlanDraft, r: number, location: string): Change[] {
  if (!draft.days[r] || draft.days[r]!.location === location) return [];
  return [change(draft, ["days", r, "location"], location)];
}

export function setTitle(draft: PlanDraft, title: string): Change[] {
  return draft.title === title ? [] : [change(draft, ["title"], title)];
}

// MARK: Definitions and notes

/** `reading`: a reused definition's, so the Worker keeps the coach's picks when it reads the lines again (§61.3). */
export function addDefinition(draft: PlanDraft, label: string, lines: string[] = [], index = draft.sessions.length, reading?: DefinitionReading): Change[] {
  const sessions = [...draft.sessions];
  sessions.splice(index, 0, reading ? ({ label, lines, reading: structuredClone(reading) } as PlanDefinition) : { label, lines });
  return [change(draft, ["sessions"], sessions)];
}

/** A label renamed everywhere at once: its definition and every chip that names it. */
export function renameLabel(draft: PlanDraft, i: number, label: string): Change[] {
  const definition = draft.sessions[i];
  if (!definition || definition.label === label || !label.trim()) return [];
  const oldKey = labelKey(definition.label);
  const shown = displayLabel(label.trim());
  const out: Change[] = [change(draft, ["sessions", i, "label"], shown)];
  draft.days.forEach((day, r) => {
    for (const column of draft.columns) {
      const chips = day.cells[column.id];
      if (!chips?.some((chip) => labelKey(chip) === oldKey)) continue;
      out.push(change(draft, ["days", r, "cells", column.id], chips.map((chip) => (labelKey(chip) === oldKey ? shown : chip))));
    }
  });
  return out;
}

export function deleteDefinition(draft: PlanDraft, i: number): Change[] {
  if (!draft.sessions[i]) return [];
  return [change(draft, ["sessions"], draft.sessions.filter((_, j) => j !== i))];
}

export function moveDefinition(draft: PlanDraft, i: number, by: number): Change[] {
  const to = i + by;
  if (!draft.sessions[i] || to < 0 || to >= draft.sessions.length) return [];
  const sessions = [...draft.sessions];
  const [moved] = sessions.splice(i, 1);
  sessions.splice(to, 0, moved!);
  return [change(draft, ["sessions"], sessions)];
}

export function addNote(draft: PlanDraft, title = ""): Change[] {
  return [change(draft, ["notes"], [...draft.notes, { title, lines: [] }])];
}

export function setNote(draft: PlanDraft, i: number, note: { title?: string; lines?: string[] }): Change[] {
  const current = draft.notes[i];
  if (!current) return [];
  const out: Change[] = [];
  if (note.title !== undefined && note.title !== current.title) out.push(change(draft, ["notes", i, "title"], note.title));
  if (note.lines !== undefined) {
    const c = change(draft, ["notes", i, "lines"], note.lines);
    if (!isNoop(c)) out.push(c);
  }
  return out;
}

export function deleteNote(draft: PlanDraft, i: number): Change[] {
  if (!draft.notes[i]) return [];
  return [change(draft, ["notes"], draft.notes.filter((_, j) => j !== i))];
}

/** A textarea's text as the draft's lines; trailing blank lines are the coach still typing. */
export function textToLines(text: string): string[] {
  const lines = text.replace(/\r\n?/g, "\n").split("\n");
  while (lines.length && lines[lines.length - 1]!.trim() === "") lines.pop();
  return lines;
}
